import React, { useState, useCallback } from 'react'
import { Box, Button, TextField, MenuItem, Select, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core'
import ViewHeadlineIcon from '@material-ui/icons/ViewHeadline'
import AppsIcon from '@material-ui/icons/Apps'
import { useStyles } from './styles'
import {
  SortOrder,
  VIEW_MODES,
  VIEW_MODE_LABELS,
  SUCCESS_MAP_KEY,
  FOURXX_MAP_KEY,
  HRT_MAP_KEY,
  FROM_DEFAULT,
  TO_DEFAULT,
  ALL_ACCOUNTS_LABEL,
  CSV_FILE_PREFIX,
  HRT_CSV_FILE_PREFIX,
  GRAND_TOTAL_LABEL,
  TOTAL_SUCCESS_LABEL,
  TOTAL_FOURXX_LABEL,
  TOTAL_HRT_LABEL,
  HRT_SECTION_TITLE,
  STAT_LABELS,
  EMPTY_TABLE_TEXT,
  HRT_EMPTY_TABLE_TEXT,
  FETCH_ERROR_MESSAGE,
  MONTHS,
} from './constants'
import { getErrorMessage } from '../constants'
import TableData from '../TableData'
import Search from '../../../../elements/icons/Search'
import Download from '../../../../elements/icons/Download'
import { DisplayMessage, Loader } from '../../../../Components'
import { eventService } from '../../../../Services/EventService'
import { connect } from 'react-redux'
import * as messageTypes from '../../../../utils/messageTypes'

const fmtMonth = (k) => {
  if (!k || k.length !== 6) return k
  return MONTHS[parseInt(k.slice(4, 6)) - 1] + " '" + k.slice(2, 4)
}

const fmtNum = (n) => {
  if (n == null || n === '') return ''
  if (n === 0) return '0'
  if (n >= 1e9) return (n / 1e9).toFixed(2).replace(/\.?0+$/, '') + 'B'
  if (n >= 1e6) return (n / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M'
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.?0+$/, '') + 'K'
  return n.toLocaleString()
}

const escapeCsvValue = (v) => {
  const s = v == null ? '' : String(v)
  return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
}

const getSuccessHmClass = (ratio, val, classes) => {
  if (val === 0) return classes.hm0
  if (ratio < 0.01) return classes.hm1
  if (ratio < 0.05) return classes.hm2
  if (ratio < 0.2) return classes.hm3
  if (ratio < 0.5) return classes.hm4
  if (ratio < 0.8) return classes.hm5
  return classes.hm6
}

const getTelemHmClass = (ratio, val, classes) => {
  if (val === 0) return classes.hmt0
  if (ratio < 0.01) return classes.hmt1
  if (ratio < 0.05) return classes.hmt2
  if (ratio < 0.2) return classes.hmt3
  if (ratio < 0.5) return classes.hmt4
  if (ratio < 0.8) return classes.hmt5
  return classes.hmt6
}

const getHrtHmClass = (ratio, val, classes) => {
  if (val === 0) return classes.hmo0
  if (ratio < 0.1) return classes.hmo1
  if (ratio < 0.25) return classes.hmo2
  if (ratio < 0.4) return classes.hmo3
  if (ratio < 0.6) return classes.hmo4
  if (ratio < 0.8) return classes.hmo5
  return classes.hmo6
}

const ApiHitsViewer = (props) => {
  const classes = useStyles()

  const [allData, setAllData] = useState([])
  const [months, setMonths] = useState([])
  const [viewMode, setViewMode] = useState(VIEW_MODES.BOTH)
  const [sortBy, setSortBy] = useState(null)
  const [sortOrder, setSortOrder] = useState(SortOrder.DESC)
  const [accountFilter, setAccountFilter] = useState('')
  const [fromMonth, setFromMonth] = useState(FROM_DEFAULT)
  const [toMonth, setToMonth] = useState(TO_DEFAULT)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('')
  const [expandedDrillDown, setExpandedDrillDown] = useState(null)
  const [drillDownCache, setDrillDownCache] = useState({})
  const [drillDownLoading, setDrillDownLoading] = useState({})
  const [expandedProgDrillDown, setExpandedProgDrillDown] = useState(null)
  const [progDrillDownCache, setProgDrillDownCache] = useState({})
  const [progDrillDownLoading, setProgDrillDownLoading] = useState({})

  const processResponse = useCallback((json) => {
    const data = json.data ?? []
    setAllData(data)

    const mSet = new Set()
    data.forEach(r => {
      Object.keys(r[SUCCESS_MAP_KEY] ?? {}).forEach(m => mSet.add(m))
      Object.keys(r[FOURXX_MAP_KEY] ?? {}).forEach(m => mSet.add(m))
    })
    setMonths([...mSet].sort())
    setSortBy(null)
    setSortOrder(SortOrder.DESC)
    setAccountFilter('')
  }, [])

  const fetchData = () => {
    const params = {}
    const from = fromMonth.trim()
    const to = toMonth.trim()
    if (from) params.from = from
    if (to) params.to = to

    setLoading(true)
    eventService.getMonthlyApiHits(params)
      .then(data => {
        processResponse(data)
        setMessage(`Loaded ${data.data?.length ?? 0} account(s)`)
        setMessageType(messageTypes.SUCCESS)
        setOpen(true)
      })
      .catch(error => {
        setMessage(getErrorMessage(error, FETCH_ERROR_MESSAGE))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
      })
      .finally(() => setLoading(false))
  }

  const handleDrillDown = (item, monthKey) => {
    const key = `${item.accountId}__${monthKey}`

    // Toggle: if same key is already expanded, collapse it
    if (expandedDrillDown === key) {
      setExpandedDrillDown(null)
      return
    }

    setExpandedDrillDown(key)

    // If already cached, no need to fetch
    if (drillDownCache[key]) return

    setDrillDownLoading(prev => ({ ...prev, [key]: true }))

    eventService.getDailyApiHits(props.currentUser.account_id, item.accountId, monthKey)
      .then(response => {
        const apiData = response.data || {}
        const dailySuccessHits = apiData.dailySuccessHits || {}
        const daily4xxHits = apiData.daily4xxHits || {}
        
        // Get all unique date keys and sort them
        const dateKeys = [...new Set([...Object.keys(dailySuccessHits), ...Object.keys(daily4xxHits)])].sort()
        
        // Transform into array format with formatted dates
        const dailyHits = dateKeys.map(dateKey => {
          const year = dateKey.substring(0, 4)
          const month = dateKey.substring(4, 6)
          const day = dateKey.substring(6, 8)
          const formattedDate = `${year}-${month}-${day}`
          
          return {
            date: formattedDate,
            successHits: dailySuccessHits[dateKey] || 0,
            fourxxHits: daily4xxHits[dateKey] || 0
          }
        })
        
        setDrillDownCache(prev => ({ 
          ...prev, 
          [key]: { 
            ...apiData,
            dailyHits 
          } 
        }))
      })
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to load drill-down data'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
        setExpandedDrillDown(null)
      })
      .finally(() => {
        setDrillDownLoading(prev => ({ ...prev, [key]: false }))
      })
  }

  const handleProgDrillDown = (item, monthKey) => {
    const key = `${item.accountId}__${monthKey}`
    if (expandedProgDrillDown === key) {
      setExpandedProgDrillDown(null)
      return
    }
    setExpandedProgDrillDown(key)
    if (progDrillDownCache[key]) return
    setProgDrillDownLoading(prev => ({ ...prev, [key]: true }))
    eventService.getProgramMonthlyHits(monthKey, item.accountId, props.currentUser.account_id)
      .then(response => {
        const data = response.data || []
        const rows = data.map((p, i) => ({
          i: i + 1,
          accountName: p.accountName ?? item.accountName,
          accountId: p.accountId ?? item.accountId,
          programId: p.programId ?? '',
          programName: p.programName ?? '',
          month: monthKey,
          sv: p.monthlySuccessHits?.[monthKey] ?? 0,
          tv: p.monthly4xxHits?.[monthKey] ?? 0,
        }))
        setProgDrillDownCache(prev => ({ ...prev, [key]: { accountName: item.accountName, monthKey, rows } }))
      })
      .catch(error => {
        setMessage(getErrorMessage(error, 'Failed to load program data'))
        setMessageType(messageTypes.ERROR)
        setOpen(true)
        setExpandedProgDrillDown(null)
      })
      .finally(() => setProgDrillDownLoading(prev => ({ ...prev, [key]: false })))
  }

  const exportProgDrillDownCsv = (progData, accountName, monthKey) => {
    if (!progData?.rows?.length) return
    const header = ['Sr No.', 'Account Name', 'Program ID', 'Program Name', 'Hits Month', 'Monthly Success Hits', 'Monthly 4xx Hits'].map(escapeCsvValue).join(',')
    const body = progData.rows.map(r => [
      r.i,
      escapeCsvValue(r.accountName ?? ''),
      r.programId ?? '',
      escapeCsvValue(r.programName ?? ''),
      r.month,
      r.sv,
      r.tv,
    ].join(','))
    const totalS = progData.rows.reduce((s, r) => s + r.sv, 0)
    const totalT = progData.rows.reduce((s, r) => s + r.tv, 0)
    const foot = [escapeCsvValue(`Total (${progData.rows.length} programs)`), '', '', '', '', totalS, totalT].join(',')
    const csv = [header, ...body, foot].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `prog-hits-${(accountName ?? '').replace(/[^a-z0-9]/gi, '-')}-${monthKey}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const exportDrillDownCsv = (drillData, accountName, monthKey) => {
    if (!drillData || !drillData.dailyHits || drillData.dailyHits.length === 0) return

    const headers = ['Date', 'Success Hits', '4xx Hits', 'Total Hits']
    const rows = drillData.dailyHits.map(day => {
      const date = day.date || ''
      const success = day.successHits || 0
      const fourxx = day.fourxxHits || 0
      const total = success + fourxx
      return `${escapeCsvValue(date)},${success},${fourxx},${total}`
    })
    const csvContent = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = downloadUrl
    downloadLink.download = `daily-hits-${accountName || 'account'}-${monthKey || ''}.csv`
    downloadLink.click()
    URL.revokeObjectURL(downloadUrl)
  }

  const getFilteredData = useCallback(() => {
    return accountFilter ? allData.filter(r => r.accountName === accountFilter) : allData
  }, [accountFilter, allData])

  const getSortedData = useCallback((rows) => {
    if (sortBy === null) return rows
    return [...rows].sort((a, b) => {
      let av, bv
      if (sortBy === 'totalS') {
        av = Object.values(a[SUCCESS_MAP_KEY] ?? {}).reduce((s, v) => s + v, 0)
        bv = Object.values(b[SUCCESS_MAP_KEY] ?? {}).reduce((s, v) => s + v, 0)
      } else if (sortBy === 'totalT') {
        av = Object.values(a[FOURXX_MAP_KEY] ?? {}).reduce((s, v) => s + v, 0)
        bv = Object.values(b[FOURXX_MAP_KEY] ?? {}).reduce((s, v) => s + v, 0)
      } else {
        const mapKey = viewMode === VIEW_MODES.FOURXX ? FOURXX_MAP_KEY : SUCCESS_MAP_KEY
        av = a[mapKey]?.[sortBy] ?? 0
        bv = b[mapKey]?.[sortBy] ?? 0
      }
      if (av < bv) return sortOrder === SortOrder.ASC ? -1 : 1
      if (av > bv) return sortOrder === SortOrder.ASC ? 1 : -1
      return 0
    })
  }, [sortBy, sortOrder, viewMode])

  const handleSortChange = (col, dir) => {
    setSortBy(col)
    setSortOrder(dir)
  }

  const filteredData = getFilteredData()
  const sortedData = getSortedData(filteredData)

  // Compute stats
  let totalSuccess = 0, totalFourxx = 0
  filteredData.forEach(r => {
    Object.values(r[SUCCESS_MAP_KEY] ?? {}).forEach(v => { totalSuccess += v })
    Object.values(r[FOURXX_MAP_KEY] ?? {}).forEach(v => { totalFourxx += v })
  })

  // Compute per-column max for heat map
  const colMax = {}
  months.forEach(m => {
    colMax[m] = Math.max(1, ...sortedData.map(r =>
      (viewMode === VIEW_MODES.FOURXX ? r[FOURXX_MAP_KEY] : r[SUCCESS_MAP_KEY])?.[m] ?? 0
    ))
  })

  // Compute grand totals per month
  const grandS = {}, grandT = {}
  months.forEach(m => {
    grandS[m] = sortedData.reduce((s, r) => s + (r[SUCCESS_MAP_KEY]?.[m] ?? 0), 0)
    grandT[m] = sortedData.reduce((s, r) => s + (r[FOURXX_MAP_KEY]?.[m] ?? 0), 0)
  })
  const grandTotalS = Object.values(grandS).reduce((s, v) => s + v, 0)
  const grandTotalT = Object.values(grandT).reduce((s, v) => s + v, 0)

  // Build table headings
  const tableHeadings = [
    { id: 'account', label: 'Account', sortOrder: null },
    ...months.map(m => ({
      id: m,
      label: fmtMonth(m),
      sortOrder: sortBy === m ? sortOrder : SortOrder.ASC,
    })),
  ]
  if (viewMode !== VIEW_MODES.FOURXX) {
    tableHeadings.push({ id: 'totalS', label: TOTAL_SUCCESS_LABEL, sortOrder: sortBy === 'totalS' ? sortOrder : SortOrder.ASC })
  }
  if (viewMode !== VIEW_MODES.SUCCESS) {
    tableHeadings.push({ id: 'totalT', label: TOTAL_FOURXX_LABEL, sortOrder: sortBy === 'totalT' ? sortOrder : SortOrder.ASC })
  }

  // Build table contents
  const tableContents = sortedData.map((r, idx) => ({
    ...r,
    id: r.accountId || idx,
    _isGrandTotal: false,
  }))
  if (sortedData.length > 0) {
    tableContents.push({
      id: '__grand_total__',
      _isGrandTotal: true,
      _count: sortedData.length,
    })
  }

  const renderMonthCellWithDrillDown = (item, heading, content, hasData) => {
    if (!hasData) {
      return (
        <Box className={classes.drillDownCellWrapper}>
          <Box className={classes.hitWrap}><Box component="span" className={classes.hitSuccess}>—</Box></Box>
        </Box>
      )
    }
    return (
      <Box className={classes.drillDownCellWrapper}>
        {content}
        <Box
          component="span"
          className={classes.drillDownChip}
          onClick={(e) => {
            e.stopPropagation()
            handleDrillDown(item, heading.id)
          }}
        >
          <ViewHeadlineIcon className={classes.drillDownChipIcon} />
        </Box>
        <Box
          component="span"
          className={classes.progDrillDownChip}
          onClick={(e) => {
            e.stopPropagation()
            handleProgDrillDown(item, heading.id)
          }}
        >
          <AppsIcon className={classes.drillDownChipIcon} />
        </Box>
      </Box>
    )
  }

  const renderCellContent = (item, heading) => {
    if (item._isGrandTotal) {
      if (heading.id === 'account') {
        return `${GRAND_TOTAL_LABEL} (${item._count} accounts)`
      }
      if (heading.id === 'totalS') return fmtNum(grandTotalS)
      if (heading.id === 'totalT') return fmtNum(grandTotalT)
      const sv = grandS[heading.id] ?? 0
      const tv = grandT[heading.id] ?? 0
      if (viewMode === VIEW_MODES.BOTH) {
        return (
          <Box>
            {fmtNum(sv)}
            <Box className={classes.hitFourxx}>{fmtNum(tv)}</Box>
          </Box>
        )
      }
      return viewMode === VIEW_MODES.SUCCESS ? fmtNum(sv) : fmtNum(tv)
    }

    if (heading.id === 'account') {
      return (
        <Box>
          <Box className={classes.acctName}>{item.accountName}</Box>
          <Box className={classes.acctId}>{item.accountId ?? ''}</Box>
        </Box>
      )
    }

    if (heading.id === 'totalS') {
      const total = Object.values(item[SUCCESS_MAP_KEY] ?? {}).reduce((s, v) => s + v, 0)
      return <Box component="span" className={classes.totalSuccessVal}>{fmtNum(total)}</Box>
    }
    if (heading.id === 'totalT') {
      const total = Object.values(item[FOURXX_MAP_KEY] ?? {}).reduce((s, v) => s + v, 0)
      return <Box component="span" className={classes.totalFourxxVal}>{fmtNum(total)}</Box>
    }

    const sv = item[SUCCESS_MAP_KEY]?.[heading.id] ?? 0
    const tv = item[FOURXX_MAP_KEY]?.[heading.id] ?? 0

    const isMonthColumn = months.includes(heading.id)
    if (viewMode === VIEW_MODES.BOTH) {
      if (sv === 0 && tv === 0) {
        return <Box className={classes.hitWrap}><Box component="span" className={classes.hitSuccess}>—</Box></Box>
      }
      const content = (
        <Box className={classes.hitWrap}>
          <Box component="span" className={classes.hitSuccess}>{fmtNum(sv)}</Box>
          <Box component="span" className={classes.hitFourxx}>{fmtNum(tv)}</Box>
        </Box>
      )
      if (isMonthColumn) {
        return renderMonthCellWithDrillDown(item, heading, content, true)
      }
      return content
    }
    if (viewMode === VIEW_MODES.SUCCESS) {
      if (isMonthColumn) {
        const content = <Box className={classes.hitWrap}><Box component="span" className={classes.hitSuccess}>{fmtNum(sv)}</Box></Box>
        return renderMonthCellWithDrillDown(item, heading, content, sv !== 0)
      }
      return <Box className={classes.hitWrap}><Box component="span" className={classes.hitSuccess}>{sv === 0 ? '—' : fmtNum(sv)}</Box></Box>
    }
    if (isMonthColumn) {
      const content = <Box className={classes.hitWrap}><Box component="span" className={classes.hitSuccess}>{fmtNum(tv)}</Box></Box>
      return renderMonthCellWithDrillDown(item, heading, content, tv !== 0)
    }
    return <Box className={classes.hitWrap}><Box component="span" className={classes.hitSuccess}>{tv === 0 ? '—' : fmtNum(tv)}</Box></Box>
  }

  const getCellClassName = (item, heading) => {
    if (item._isGrandTotal) {
      return heading.id === 'account' ? classes.grandTotalAccountCell : classes.grandTotalCell
    }
    if (heading.id === 'account') return classes.accountCell
    if (heading.id === 'totalS') return `${classes.titleWrapper} ${classes.totalSuccessCol}`
    if (heading.id === 'totalT') return `${classes.titleWrapper} ${classes.totalFourxxCol}`

    const sv = item[SUCCESS_MAP_KEY]?.[heading.id] ?? 0
    const tv = item[FOURXX_MAP_KEY]?.[heading.id] ?? 0
    const hmVal = viewMode === VIEW_MODES.FOURXX ? tv : sv
    const ratio = hmVal / (colMax[heading.id] || 1)
    const hmClass = viewMode === VIEW_MODES.FOURXX
      ? getTelemHmClass(ratio, hmVal, classes)
      : getSuccessHmClass(ratio, hmVal, classes)

    return `${classes.dataCell} ${hmClass}`
  }

  const renderExpandedRow = (item, index, colSpan) => {
    if (item._isGrandTotal || !item.accountId) return null

    const activeDailyKey = expandedDrillDown?.startsWith(`${item.accountId}__`) ? expandedDrillDown : null
    const activeProgKey = expandedProgDrillDown?.startsWith(`${item.accountId}__`) ? expandedProgDrillDown : null

    if (!activeDailyKey && !activeProgKey) return null

    const dailyMonthKey = activeDailyKey?.split('__')[1]
    const isDailyLoading = activeDailyKey ? drillDownLoading[activeDailyKey] : false
    const drillData = activeDailyKey ? drillDownCache[activeDailyKey] : null
    const dailyHits = drillData?.dailyHits || []
    const totalSuccess = dailyHits.reduce((sum, day) => sum + (day.successHits || 0), 0)
    const totalFourxx = dailyHits.reduce((sum, day) => sum + (day.fourxxHits || 0), 0)
    const daysCount = dailyHits.length

    const progMonthKey = activeProgKey?.split('__')[1]
    const isProgLoading = activeProgKey ? progDrillDownLoading[activeProgKey] : false
    const progData = activeProgKey ? progDrillDownCache[activeProgKey] : null
    const progRows = progData?.rows || []
    const progTotalS = progRows.reduce((s, r) => s + r.sv, 0)
    const progTotalT = progRows.reduce((s, r) => s + r.tv, 0)

    return (
      <TableRow key={`expand-${item.accountId}`} className={classes.expandRow}>
        <TableCell colSpan={colSpan} className={classes.drillDownExpandCell}>

          {activeDailyKey && (
            <Box className={classes.drillDownExpandInner}>
              <Box className={classes.drillDownHeader}>
                <Box className={classes.drillDownHeaderTitle}>
                  DAILY BREAKDOWN — {item.accountName} / {fmtMonth(dailyMonthKey)}
                </Box>
                <Button
                  className={classes.drillDownExportBtn}
                  onClick={() => exportDrillDownCsv(drillData, item.accountName, dailyMonthKey)}
                  disabled={daysCount === 0}
                >
                  <Download className={classes.download} />
                  Export CSV
                </Button>
              </Box>
              {isDailyLoading && (
                <Box className={classes.drillDownLoadingContainer}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!isDailyLoading && drillData && (
                <Box className={classes.drillDownTablesContainer}>
                  {daysCount > 0 ? (
                    <Box className={classes.drillDownNestedTableWrap}>
                      <Table size="small" className={classes.drillDownNestedTable}>
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.drillDownNestedTh}>DATE</TableCell>
                            <TableCell className={classes.drillDownNestedTh}>SUCCESS HITS</TableCell>
                            <TableCell className={classes.drillDownNestedTh}>4XX HITS</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dailyHits.map((day, idx) => (
                            <TableRow key={`day-${day.date || idx}`}>
                              <TableCell className={classes.drillDownNestedTd}>{day.date || ''}</TableCell>
                              <TableCell className={classes.drillDownNestedTd}>{(day.successHits || 0).toLocaleString()}</TableCell>
                              <TableCell className={classes.drillDownNestedTd}>{(day.fourxxHits || 0).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className={classes.drillDownTotalRow}>
                            <TableCell className={classes.drillDownTotalCell}>Total ({daysCount} days)</TableCell>
                            <TableCell className={classes.drillDownTotalCell}>{totalSuccess.toLocaleString()}</TableCell>
                            <TableCell className={classes.drillDownTotalCell}>{totalFourxx.toLocaleString()}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Box className={classes.drillDownEmpty}>No daily data available</Box>
                  )}
                </Box>
              )}
              {!isDailyLoading && !drillData && (
                <Box className={classes.drillDownEmpty}>No data available</Box>
              )}
            </Box>
          )}

          {activeProgKey && (
            <Box className={`${classes.drillDownExpandInner}`}>
              <Box className={classes.drillDownHeader}>
                <Box className={classes.progDrillDownHeaderTitle}>
                  PROGRAM BREAKDOWN — {item.accountName} | {fmtMonth(progMonthKey)}
                  {progRows.length > 0 && (
                    <Box component="span" className={classes.progCountBadge}>{progRows.length} program{progRows.length !== 1 ? 's' : ''}</Box>
                  )}
                </Box>
                <Button
                  className={classes.drillDownExportBtn}
                  onClick={() => exportProgDrillDownCsv(progData, item.accountName, progMonthKey)}
                  disabled={progRows.length === 0}
                >
                  <Download className={classes.download} />
                  Export CSV
                </Button>
              </Box>
              {isProgLoading && (
                <Box className={classes.drillDownLoadingContainer}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!isProgLoading && progData && (
                <Box className={classes.drillDownTablesContainer}>
                  {progRows.length > 0 ? (
                    <Box className={classes.drillDownNestedTableWrap}>
                      <Table size="small" className={classes.drillDownNestedTable}>
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.progNestedTh}>SR NO.</TableCell>
                            <TableCell className={classes.progNestedTh}>ACCOUNT NAME</TableCell>
                            <TableCell className={classes.progNestedTh}>PROGRAM ID</TableCell>
                            <TableCell className={classes.progNestedTh}>PROGRAM NAME</TableCell>
                            <TableCell className={classes.progNestedTh}>HITS MONTH</TableCell>
                            <TableCell className={classes.progNestedTh}>MONTHLY SUCCESS HITS</TableCell>
                            <TableCell className={classes.progNestedTh}>MONTHLY 4XX HITS</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {progRows.map((r, idx) => (
                            <TableRow key={`prog-${r.programId || idx}`}>
                              <TableCell className={classes.progNestedTd}>{r.i}</TableCell>
                              <TableCell className={classes.progNestedTd}>{r.accountName}</TableCell>
                              <TableCell className={`${classes.progNestedTd} ${classes.progIdCell}`}>{r.programId}</TableCell>
                              <TableCell className={classes.progNestedTd}>{r.programName}</TableCell>
                              <TableCell className={classes.progNestedTd}>{fmtMonth(r.month)}</TableCell>
                              <TableCell className={`${classes.progNestedTd} ${classes.progSuccessVal}`}>{r.sv.toLocaleString()}</TableCell>
                              <TableCell className={`${classes.progNestedTd} ${classes.progFourxxVal}`}>{r.tv.toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className={classes.progTotalRow}>
                            <TableCell colSpan={5} className={classes.progTotalCell}>Total ({progRows.length} programs)</TableCell>
                            <TableCell className={`${classes.progTotalCell} ${classes.progSuccessVal}`}>{progTotalS.toLocaleString()}</TableCell>
                            <TableCell className={`${classes.progTotalCell} ${classes.progFourxxVal}`}>{progTotalT.toLocaleString()}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  ) : (
                    <Box className={classes.drillDownEmpty}>No program data available</Box>
                  )}
                </Box>
              )}
              {!isProgLoading && !progData && (
                <Box className={classes.drillDownEmpty}>No data available</Box>
              )}
            </Box>
          )}

        </TableCell>
      </TableRow>
    )
  }

  // CSV export
  const exportCsv = () => {
    if (!allData.length) return
    const rows = filteredData
    const monthHeaders = months.flatMap(m => [`${fmtMonth(m)} Success`, `${fmtMonth(m)} 4xx`])
    const header = ['Account Name', 'Account ID', ...monthHeaders, 'Total Success', 'Total 4xx'].map(escapeCsvValue).join(',')
    const body = rows.map(r => {
      const sh = r[SUCCESS_MAP_KEY] ?? {}
      const th = r[FOURXX_MAP_KEY] ?? {}
      let tS = 0, tT = 0
      const monthVals = months.flatMap(m => {
        const sv = sh[m] ?? ''
        const tv = th[m] ?? ''
        if (sh[m]) tS += sh[m]
        if (th[m]) tT += th[m]
        return [sv, tv]
      })
      return [escapeCsvValue(r.accountName ?? ''), r.accountId ?? '', ...monthVals, tS || '', tT || ''].join(',')
    })
    const grandVals = months.flatMap(m => [
      rows.reduce((s, r) => s + (r[SUCCESS_MAP_KEY]?.[m] ?? 0), 0),
      rows.reduce((s, r) => s + (r[FOURXX_MAP_KEY]?.[m] ?? 0), 0),
    ])
    const gTS = rows.reduce((s, r) => s + Object.values(r[SUCCESS_MAP_KEY] ?? {}).reduce((a, b) => a + b, 0), 0)
    const gTT = rows.reduce((s, r) => s + Object.values(r[FOURXX_MAP_KEY] ?? {}).reduce((a, b) => a + b, 0), 0)
    const grandRow = [GRAND_TOTAL_LABEL, '', ...grandVals, gTS, gTT].join(',')
    const csv = [header, ...body, grandRow].join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${CSV_FILE_PREFIX}_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // HRT table
  const hrtRows = allData.filter(r => r[HRT_MAP_KEY] && Object.keys(r[HRT_MAP_KEY]).length > 0)
  const hrtMonths = (() => {
    const mSet = new Set()
    hrtRows.forEach(r => Object.keys(r[HRT_MAP_KEY]).forEach(m => mSet.add(m)))
    return [...mSet].sort()
  })()

  const hrtColMax = {}
  hrtMonths.forEach(m => {
    hrtColMax[m] = Math.max(1, ...hrtRows.map(r => r[HRT_MAP_KEY][m] ?? 0))
  })

  const hrtGrandVals = {}
  hrtMonths.forEach(m => {
    hrtGrandVals[m] = hrtRows.reduce((s, r) => s + (r[HRT_MAP_KEY][m] ?? 0), 0)
  })
  const hrtGrandTotal = Object.values(hrtGrandVals).reduce((s, v) => s + v, 0)

  const hrtTableHeadings = [
    { id: 'account', label: 'Account', sortOrder: null },
    ...hrtMonths.map(m => ({ id: m, label: fmtMonth(m), sortOrder: null })),
    { id: 'totalHrt', label: TOTAL_HRT_LABEL, sortOrder: null },
  ]

  const hrtTableContents = hrtRows.map((r, idx) => ({
    ...r,
    id: r.accountId || `hrt-${idx}`,
    _isGrandTotal: false,
  }))
  if (hrtRows.length > 0) {
    hrtTableContents.push({
      id: '__hrt_grand_total__',
      _isGrandTotal: true,
      _count: hrtRows.length,
    })
  }

  const renderHrtCellContent = (item, heading) => {
    if (item._isGrandTotal) {
      if (heading.id === 'account') return `${GRAND_TOTAL_LABEL} (${item._count} accounts)`
      if (heading.id === 'totalHrt') return hrtGrandTotal.toLocaleString()
      return (hrtGrandVals[heading.id] ?? 0).toLocaleString()
    }
    if (heading.id === 'account') {
      return (
        <Box>
          <Box className={classes.acctName}>{item.accountName}</Box>
          <Box className={classes.acctId}>{item.accountId ?? ''}</Box>
        </Box>
      )
    }
    if (heading.id === 'totalHrt') {
      const total = Object.values(item[HRT_MAP_KEY] ?? {}).reduce((s, v) => s + v, 0)
      return <Box component="span" className={classes.totalHrtVal}>{total.toLocaleString()}</Box>
    }
    const v = item[HRT_MAP_KEY]?.[heading.id] ?? 0
    return (
      <Box className={classes.hitWrap}>
        <Box component="span" className={classes.hitSuccess}>{v === 0 ? '—' : v.toLocaleString()}</Box>
      </Box>
    )
  }

  const getHrtCellClassName = (item, heading) => {
    if (item._isGrandTotal) {
      return heading.id === 'account' ? classes.grandTotalAccountCell : classes.grandTotalCell
    }
    if (heading.id === 'account') return classes.accountCell
    if (heading.id === 'totalHrt') return `${classes.titleWrapper} ${classes.totalHrtCol}`
    const v = item[HRT_MAP_KEY]?.[heading.id] ?? 0
    const ratio = v / (hrtColMax[heading.id] || 1)
    return `${classes.dataCell} ${getHrtHmClass(ratio, v, classes)}`
  }

  const exportHrtCsv = () => {
    if (!hrtRows.length) return
    const header = ['Account Name', 'Account ID', ...hrtMonths.map(m => fmtMonth(m) + ' Sites'), TOTAL_HRT_LABEL].map(escapeCsvValue).join(',')
    const body = hrtRows.map(r => {
      let total = 0
      const vals = hrtMonths.map(m => {
        const v = r[HRT_MAP_KEY][m] ?? ''
        if (r[HRT_MAP_KEY][m]) total += r[HRT_MAP_KEY][m]
        return v
      })
      return [escapeCsvValue(r.accountName ?? ''), r.accountId ?? '', ...vals, total || ''].join(',')
    })
    const grandVals = hrtMonths.map(m => hrtRows.reduce((s, r) => s + (r[HRT_MAP_KEY][m] ?? 0), 0))
    const grandTotal = grandVals.reduce((s, v) => s + v, 0)
    const grandRow = [GRAND_TOTAL_LABEL, '', ...grandVals, grandTotal].join(',')
    const csv = [header, ...body, grandRow].join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${HRT_CSV_FILE_PREFIX}_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const accountNames = allData.map(r => r.accountName).filter(Boolean).sort()

  return (
    <Box>
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <Loader loading={loading} />

      <Box className={classes.urlBar}>
        <Box className={classes.urlRow}>
          <Box className={classes.urlBarLabel}>From</Box>
          <TextField
            className={classes.urlInput}
            value={fromMonth}
            onChange={(e) => setFromMonth(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
          <Box className={classes.urlBarLabel}>To</Box>
          <TextField
            className={classes.urlInput}
            value={toMonth}
            onChange={(e) => setToMonth(e.target.value)}
            variant="standard"
            InputProps={{ disableUnderline: true }}
          />
          <Button className={classes.submitBtn} onClick={fetchData} disabled={loading}>
            <Search />
            <Box paddingLeft='10px'>Fetch & Render</Box>
          </Button>
        </Box>
      </Box>

      {allData.length > 0 && (
        <Box className={classes.statsBar}>
          <Box component="span" className={classes.statPill}>
            <Box component="span" className={classes.statPillStrong}>{filteredData.length.toLocaleString()}</Box>&nbsp;{STAT_LABELS.ACCOUNTS}
          </Box>
          <Box component="span" className={`${classes.statPill} ${classes.statPillAmber}`}>
            <Box component="span" className={classes.statPillStrong}>{months.length}</Box>&nbsp;{STAT_LABELS.MONTHS}
          </Box>
          <Box component="span" className={`${classes.statPill} ${classes.statPillGreen}`}>
            {STAT_LABELS.TOTAL_SUCCESS}: <Box component="span" className={classes.statPillStrong}>{fmtNum(totalSuccess)}</Box>
          </Box>
          <Box component="span" className={`${classes.statPill} ${classes.statPillPurple}`}>
            {STAT_LABELS.TOTAL_FOURXX}: <Box component="span" className={classes.statPillStrong}>{fmtNum(totalFourxx)}</Box>
          </Box>
        </Box>
      )}

      <Box className={classes.container}>
        <Box className={classes.card}>
          <Box className={classes.filterBar}>
            <Select
              className={classes.filterSelect}
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              displayEmpty
              disableUnderline
              MenuProps={{
                classes: { paper: classes.selectPaper },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value="">{ALL_ACCOUNTS_LABEL}</MenuItem>
              {accountNames.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>

            <Box className={classes.toggleGroup}>
              {Object.entries(VIEW_MODE_LABELS).map(([mode, label]) => (
                <Button
                  key={mode}
                  className={`${classes.toggleBtn} ${viewMode === mode ? (mode === VIEW_MODES.FOURXX ? classes.toggleBtnActiveTelem : classes.toggleBtnActive) : ''}`}
                  onClick={() => setViewMode(mode)}
                >
                  {label}
                </Button>
              ))}
            </Box>

            <Box className={classes.filterRight}>
              <Button className={classes.exportBtn} onClick={exportCsv}>
                <Download />
                <Box paddingLeft='5px'>Export CSV</Box>
              </Button>
            </Box>
          </Box>

          <TableData
            classes={classes}
            tableHeadings={tableHeadings}
            tableContents={tableContents}
            isBold={sortBy}
            handleSortChange={handleSortChange}
            renderCellContent={renderCellContent}
            getCellClassName={getCellClassName}
            renderExpandedRow={renderExpandedRow}
            containerClassName={classes.tableWrap}
            tableClassName={classes.apiHitsTable}
            stickyHeader={false}
            emptyTableText={EMPTY_TABLE_TEXT}
          />
        </Box>

        {hrtRows.length > 0 && (
          <Box className={classes.hrtCard}>
            <Box className={classes.hrtSectionTitle}>
              {HRT_SECTION_TITLE}
              <Box component="span" className={classes.hrtBadge}>{hrtRows.length} account{hrtRows.length > 1 ? 's' : ''}</Box>
            </Box>
            <Box className={classes.hrtFilterBar}>
              <Button className={classes.exportHrtBtn} onClick={exportHrtCsv}>
                <Download />
                <Box paddingLeft='5px'>Export HRT CSV</Box>
              </Button>
            </Box>
            <TableData
              classes={classes}
              tableHeadings={hrtTableHeadings}
              tableContents={hrtTableContents}
              isBold={null}
              renderCellContent={renderHrtCellContent}
              getCellClassName={getHrtCellClassName}
              containerClassName={classes.tableWrap}
              tableClassName={classes.hrtTable}
              stickyHeader={false}
              emptyTableText={HRT_EMPTY_TABLE_TEXT}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser } = auth
  return { currentUser }
}

export default connect(mapStateToProps)(ApiHitsViewer)
